import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Separator } from '@/components/ui/separator';
import { CheckCircle, AlertTriangle, Eye, DollarSign } from 'lucide-react';
import { usePayrollCalculations } from '@/hooks/usePayrollCalculations';
import { usePayrollPeriods } from '@/hooks/usePayrollPeriods';
import { format } from 'date-fns';

interface PayrollApprovalWorkflowProps {
  payrollPeriodId: string;
}

export function PayrollApprovalWorkflow({ payrollPeriodId }: PayrollApprovalWorkflowProps) {
  const { calculations, isLoading, approvePayroll, isApproving } = usePayrollCalculations(payrollPeriodId);
  const { periods } = usePayrollPeriods();
  const [selectedCalculation, setSelectedCalculation] = useState<string>('');

  const period = periods?.find(p => p.id === payrollPeriodId);
  const selectedCalc = calculations?.find(c => c.id === selectedCalculation);

  const totalGross = calculations?.reduce((sum, calc) => sum + calc.gross_salary, 0) || 0;
  const totalNet = calculations?.reduce((sum, calc) => sum + calc.net_salary, 0) || 0;
  const totalDeductions = calculations?.reduce((sum, calc) => sum + calc.total_deductions, 0) || 0;

  const handleApproval = () => {
    if (payrollPeriodId) {
      // In a real app, you'd get the current user ID
      approvePayroll({ periodId: payrollPeriodId, approvedBy: 'current-user' });
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="text-center text-muted-foreground">Loading payroll data...</div>
        </CardContent>
      </Card>
    );
  }

  if (!period) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="text-center text-muted-foreground">Payroll period not found</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Period Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Eye className="h-5 w-5" />
            Payroll Review - {format(new Date(period.start_date), 'MMMM yyyy')}
          </CardTitle>
          <CardDescription>
            Review and approve payroll calculations for {calculations?.length || 0} employees
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-primary">
                CHF {totalGross.toFixed(2)}
              </div>
              <div className="text-sm text-muted-foreground">Total Gross</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-destructive">
                CHF {totalDeductions.toFixed(2)}
              </div>
              <div className="text-sm text-muted-foreground">Total Deductions</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">
                CHF {totalNet.toFixed(2)}
              </div>
              <div className="text-sm text-muted-foreground">Total Net</div>
            </div>
          </div>

          <Separator />

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Badge variant={period.status === 'approved' ? 'default' : 'outline'}>
                {period.status.charAt(0).toUpperCase() + period.status.slice(1)}
              </Badge>
              {period.status === 'calculated' && (
                <span className="text-sm text-amber-600 flex items-center gap-1">
                  <AlertTriangle className="h-4 w-4" />
                  Pending Approval
                </span>
              )}
            </div>

            {period.status === 'calculated' && (
              <Button
                onClick={handleApproval}
                disabled={isApproving}
                className="flex items-center gap-2"
              >
                <CheckCircle className="h-4 w-4" />
                {isApproving ? 'Approving...' : 'Approve Payroll'}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Employee Calculations */}
      <Card>
        <CardHeader>
          <CardTitle>Employee Calculations</CardTitle>
          <CardDescription>Review individual payroll calculations</CardDescription>
        </CardHeader>
        <CardContent>
          {calculations && calculations.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Employee</TableHead>
                  <TableHead>Hours</TableHead>
                  <TableHead>Gross Salary</TableHead>
                  <TableHead>Deductions</TableHead>
                  <TableHead>Net Salary</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {calculations.map((calc) => (
                  <TableRow key={calc.id}>
                    <TableCell className="font-medium">
                      {calc.employees?.name || 'Unknown Employee'}
                    </TableCell>
                    <TableCell>{calc.hours_worked?.toFixed(1) || '0.0'}</TableCell>
                    <TableCell>CHF {calc.gross_salary.toFixed(2)}</TableCell>
                    <TableCell>CHF {calc.total_deductions.toFixed(2)}</TableCell>
                    <TableCell className="font-semibold">CHF {calc.net_salary.toFixed(2)}</TableCell>
                    <TableCell>
                      <Badge variant={calc.calculation_status === 'approved' ? 'default' : 'outline'}>
                        {calc.calculation_status.charAt(0).toUpperCase() + calc.calculation_status.slice(1)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setSelectedCalculation(
                          selectedCalculation === calc.id ? '' : calc.id
                        )}
                      >
                        {selectedCalculation === calc.id ? 'Hide' : 'Details'}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              No calculations found for this period
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detailed View */}
      {selectedCalc && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5" />
              Detailed Calculation - {selectedCalc.employees?.name}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm text-muted-foreground">Base Salary</Label>
                <div className="font-medium">CHF {selectedCalc.base_salary.toFixed(2)}</div>
              </div>
              <div>
                <Label className="text-sm text-muted-foreground">Hours Worked</Label>
                <div className="font-medium">{selectedCalc.hours_worked?.toFixed(1) || '0.0'}</div>
              </div>
            </div>

            <Separator />

            <div>
              <h4 className="font-semibold mb-2">Deductions Breakdown</h4>
              <div className="space-y-2">
                {/* Note: Deductions would come from a related query in a real implementation */}
                <div className="text-sm text-muted-foreground">
                  Detailed deductions would be displayed here
                </div>
              </div>
            </div>

            <Separator />

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm text-muted-foreground">Final Gross</Label>
                <div className="text-lg font-bold">CHF {selectedCalc.gross_salary.toFixed(2)}</div>
              </div>
              <div>
                <Label className="text-sm text-muted-foreground">Final Net</Label>
                <div className="text-lg font-bold text-green-600">CHF {selectedCalc.net_salary.toFixed(2)}</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// Helper component for labels in detailed view
function Label({ children, className }: { children: React.ReactNode; className?: string }) {
  return <div className={className}>{children}</div>;
}